import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { formatCurrency, formatDecimalWithGroupedUnit } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { CompanyContractConsumptionControlledBy } from '@/features/company/types/contract.type'
import { useClientCompanyBranchAvailableToAllocateSummary } from '@/features/companyBranches/hooks/useClientCompanyBranchAvailableToAllocateSummary'
import { ClientCompanyAllowedCategoriesAndProducts } from '@/features/companyBranches/types/allowedCategoriesAndProducts.type'
import { useUpdateGovernmentAllocationEntryProductCategory } from '@/features/governmentAllocations/hooks/useUpdateGovernmentAllocationEntryProductCategory'
import { EntryProductCategory } from '@/features/governmentAllocations/pages/Update/components/EntryProductCategory'
import { EntryProductCategoryQuantity } from '@/features/governmentAllocations/pages/Update/components/EntryProductCategoryQuantity'
import { updateGovernmentAllocationEntryProductCategoryDefaultValues } from '@/features/governmentAllocations/pages/Update/utils/updateGovernmentAllocationEntryProductCategoryDefaultValues'
import {
  entryProductCategoryCurrencySchema,
  entryProductCategoryQuantitySchema,
  GovernmentAllocationEntryProductCategoryCurrencyValidationSchema,
  GovernmentAllocationEntryProductCategoryQuantityValidationSchema,
} from '@/features/governmentAllocations/schemas/update.schema'
import { GovernmentAllocationEntryProductCategory } from '@/features/governmentAllocations/types/governmentAllocationEntry.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { FileEditIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

interface UpdateEntryProductCategoryModalProps {
  companyBranchId?: string
  governmentAllocationId: string
  entryProductCategory: GovernmentAllocationEntryProductCategory
  controlledBy?: CompanyContractConsumptionControlledBy
  governmentAllocationCurrencyRestricted: boolean | undefined;
}

export const UpdateEntryProductCategoryModal = ({
  companyBranchId,
  governmentAllocationId,
  entryProductCategory,
  controlledBy = CompanyContractConsumptionControlledBy.Currency,
  governmentAllocationCurrencyRestricted,
}: UpdateEntryProductCategoryModalProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: allowedCategoriesAndProducts
  } = useClientCompanyBranchAvailableToAllocateSummary<ClientCompanyAllowedCategoriesAndProducts>({
    companyBranchId,
    filters: {
      productCategoryId: entryProductCategory?.product_category?.id,
    }
  });

  const schema = controlledBy === 'quantity' ? entryProductCategoryQuantitySchema : entryProductCategoryCurrencySchema

  const {
    mutate: updateGovernmentAllocationEntryProductCategories,
    isPending: updateGovernmentAllocationEntryProductCategoriesIsPending,
  } = useUpdateGovernmentAllocationEntryProductCategory({
    governmentAllocationId,
    entryProductCategory,
    governmentAllocationCurrencyRestricted,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentAllocation', governmentAllocationId] })
      queryClient.invalidateQueries({ queryKey: ['clientCompanyBranchAvailableToAllocateSummary'] })
      setIsOpen(false)
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    },
  })

  const formMethods = useForm<GovernmentAllocationEntryProductCategoryCurrencyValidationSchema | GovernmentAllocationEntryProductCategoryQuantityValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: updateGovernmentAllocationEntryProductCategoryDefaultValues(
      entryProductCategory,
      controlledBy,
      governmentAllocationCurrencyRestricted,
    ),
  })

  const { handleSubmit, setError, reset, watch, setValue } = formMethods

  const entryProducts = watch('entry_products')

  const setTotalValue = () => {
    setTimeout(() => {
      if (!entryProducts || entryProducts.length === 0) {
        return
      }

      if (controlledBy === CompanyContractConsumptionControlledBy.Quantity) {
        const entryProductsTotalQuantitySum = (entryProducts || []).reduce((sum, _, index) => {
          const productTotalQuantity = watch(`entry_products.${index}.total_quantity` as `entry_products.${number}.total_value`);
          return sum + (productTotalQuantity ? unFormatUnknownUnit(productTotalQuantity) : 0);
        }, 0);

        setValue('total_quantity' as 'total_value', formatDecimalWithGroupedUnit(
          entryProductsTotalQuantitySum,
          entryProducts as GovernmentAllocationEntryProductCategoryQuantityValidationSchema['entry_products'],
          (p) => p.product.unit_for_quantity
        ));
      }

      const entryProductsTotalValueSum = (entryProducts || []).reduce((sum, _, index) => {
        const productTotalValue = watch(`entry_products.${index}.total_value`);
        return sum + (productTotalValue ? unFormatCurrency(productTotalValue) : 0);
      }, 0);

      setValue('total_value', formatCurrency(entryProductsTotalValueSum));
    });
  }

  const onSubmit: SubmitHandler<GovernmentAllocationEntryProductCategoryCurrencyValidationSchema | GovernmentAllocationEntryProductCategoryQuantityValidationSchema> = (form) =>
    updateGovernmentAllocationEntryProductCategories(form)

  useEffect(() => {
    if (isOpen) {
      reset(updateGovernmentAllocationEntryProductCategoryDefaultValues(
        entryProductCategory,
        controlledBy,
        governmentAllocationCurrencyRestricted,
      ))
    }
  }, [entryProductCategory.entry_products.length, isOpen])

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          setIsOpen(value)

          if (!value) {
            setTimeout(reset, 500)
          }
        }}
        title="Detalhando crédito por serviço"
        showClose
        Trigger={
          <Button variant="outline-text" className="h-10 w-auto p-0">
            <FileEditIcon strokeWidth={1} />
          </Button>
        }
        className="min-w-[640px] overflow-hidden"
        useCancellableFooter
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonText="Salvar"
        confirmButtonClassName="bg-primary-blue-hover"
        confirmButtonIsLoading={updateGovernmentAllocationEntryProductCategoriesIsPending}
        cancelButtonClassName='bg-white-200'
        bodyClassName='max-h-[80vh] overflow-auto'
        body={
          <FormProvider {...formMethods}>
            <div className="max-h-[480px] min-h-[320px] overflow-y-auto flex flex-col gap-6 px-1 text-white-800">
              {controlledBy === CompanyContractConsumptionControlledBy.Quantity ? (
                <EntryProductCategoryQuantity
                  entryProductCategory={entryProductCategory}
                  companyBranchId={companyBranchId}
                  disabled={false}
                  allowedCategorySummary={allowedCategoriesAndProducts?.categories?.[entryProductCategory.product_category.id]}
                  onTotalValueChange={setTotalValue}
                  governmentAllocationCurrencyRestricted={governmentAllocationCurrencyRestricted}
                />
              ) : (
                <EntryProductCategory
                  companyBranchId={companyBranchId}
                  entryProductCategory={entryProductCategory}
                  disabled={updateGovernmentAllocationEntryProductCategoriesIsPending}
                  allowedCategorySummary={allowedCategoriesAndProducts?.categories?.[entryProductCategory.product_category.id]}
                  onTotalValueChange={setTotalValue}
                />
              )}
            </div>
          </FormProvider>
        }
      />

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </>
  )
}
